/**
 * Hook for checking if user needs verification (email, MFA, or phone).
 * Returns redirect URL if verification needed, null otherwise.
 */

import { useLocation } from 'react-router-dom'
import { AccountType } from '~/constants/settings'
import { useAppConfig } from '~/contexts/AppConfigContext'
import { useBillingContext } from '~/contexts/BillingContext'
import { useUserContext } from '~/contexts/UserContext'

const EXEMPT_PATHS = new Set([
  '/verify-email',
  '/setup-mfa',
  '/phone-verification',
  '/setup-payment'
])

export function useVerificationRedirect() {
  const { user } = useUserContext()
  const { config, loading: configLoading } = useAppConfig()
  const { hasLicense, loaded: billingLoaded } = useBillingContext()
  const { pathname } = useLocation()

  const isExemptPath = EXEMPT_PATHS.has(pathname)

  // Don't redirect on exempt paths or without user/config
  if (isExemptPath || !user || configLoading) {
    return { redirectUrl: null, isLoading: configLoading, needsRedirect: false }
  }

  // Check verifications in priority order: email -> MFA -> phone
  // Note: Only phone verification needs billing, check others first
  const redirectPath = getRedirectPath(user, config, billingLoaded, hasLicense(AccountType.FLEX))
  const redirectUrl = redirectPath ? buildRedirectUrl(redirectPath) : null

  return {
    redirectUrl,
    isLoading: false,
    needsRedirect: redirectUrl !== null
  }
}

function getRedirectPath(
  user: { email_verified?: boolean | null; onboarded?: boolean; phone_verified?: boolean },
  config: { email_verification_enabled: boolean; phone_verification_enabled: boolean },
  billingLoaded: boolean,
  isFreeLicense: boolean
): string | null {
  // 1. Email verification (no billing needed)
  if (config.email_verification_enabled && user.email_verified !== true) {
    return '/verify-email'
  }

  // 2. MFA setup (no billing needed)
  if (user.onboarded === false) {
    return '/setup-mfa'
  }

  // 3. Phone verification (needs billing to check flex license)
  if (
    billingLoaded &&
    config.phone_verification_enabled &&
    isFreeLicense &&
    user.phone_verified === false
  ) {
    return '/phone-verification'
  }

  return null
}

function buildRedirectUrl(path: string): string {
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
  return `${path}?return_url=${returnUrl}`
}
